import React from 'react';
import { Card, CardContent } from './ui/card';
import { Star } from 'lucide-react';
import { motion } from 'framer-motion';

const testimonials = [
  {
    name: 'High School Baseball Coach',
    role: 'HS Varsity Program',
    sport: 'Baseball',
    quote: 'This lecture helped my players block anxiety stressors that had been in their way. Since our session, we\'ve had the best practices all year — stress-free, fun, and unified as a team. Our younger players are now stepping up as leaders.',
    featured: true,
  },
  {
    name: 'Jordan M.',
    role: 'HS Varsity Player',
    sport: 'Soccer',
    quote: 'I used to freeze in big games. Now I have a routine that keeps me locked in no matter the situation. My confidence is completely different.',
    featured: false,
  },
  {
    name: 'Lisa T.',
    role: 'Parent · College Swimmer',
    sport: 'Swimming',
    quote: 'I didn\'t know how to help my daughter with her performance anxiety. Coach Lewis gave us both the tools to understand it and actually work through it.',
    featured: false,
  },
];

const fadeIn = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-30px' },
};

export const TestimonialsSection = () => {
  return (
    <section
      id="testimonials"
      className="py-16 sm:py-20 bg-[#F6F7F8]"
      data-testid="testimonials-section"
    >
      <div className="max-w-[1120px] mx-auto px-5 sm:px-6">
        <div className="text-center mb-10">
          <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#004C54] font-semibold mb-2">
            Social Proof
          </div>
          <h2 className="font-heading text-[1.65rem] sm:text-3xl font-bold tracking-tight text-foreground">
            What Athletes & Coaches Are Saying
          </h2>
        </div>

        {/* Staggered Layout */}
        <div className="space-y-4">
          {/* Featured testimonial - Full width */}
          <motion.div {...fadeIn} transition={{ duration: 0.4, delay: 0 }}>
            <Card
              className="bg-white border border-[#004C54]/15 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.08)]"
              data-testid="testimonial-card-featured"
            >
              <CardContent className="p-6 sm:p-8">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={`star-top-${j}`} className="h-4 w-4 fill-[#004C54] text-[#004C54]" />
                  ))}
                </div>
                <p className="text-[15px] sm:text-[16px] text-foreground leading-relaxed mb-6 max-w-[75ch]">
                  "{testimonials[0].quote}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-border/30">
                  <div className="relative w-12 h-12 shrink-0">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#004C54] via-[#006873] to-[#003a40] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.18)]" />
                    <div className="absolute inset-[2px] rounded-full bg-gradient-to-br from-[#005860] to-[#004047] flex items-center justify-center ring-2 ring-[#4FBCC7]/40">
                      <span className="font-heading text-[14px] font-bold text-white tracking-wide drop-shadow-sm">
                        {testimonials[0].name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[14px] sm:text-[15px] font-semibold text-foreground leading-tight">{testimonials[0].name}</p>
                    <p className="text-[12px] text-muted-foreground">
                      <span className="inline-block px-1.5 py-0.5 rounded bg-[#004C54]/8 text-[#004C54] font-semibold text-[10.5px] uppercase tracking-wider mr-1.5">{testimonials[0].sport}</span>
                      {testimonials[0].role}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Bottom two - Staggered with offset */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-5">
            {testimonials.slice(1).map((t, i) => (
              <motion.div
                key={t.name || t.quote.slice(0, 24)}
                {...fadeIn}
                transition={{ duration: 0.4, delay: (i + 1) * 0.1 }}
                className={i === 0 ? 'md:mt-6' : ''}
              >
                <Card
                  className="bg-white border border-border/50 rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] h-full"
                  data-testid={`testimonial-card-${i + 1}`}
                >
                  <CardContent className="p-5 sm:p-6 flex flex-col h-full">
                    <div className="flex gap-0.5 mb-3">
                      {[...Array(5)].map((_, j) => (
                        <Star key={`star-bottom-${t.name || ''}-${j}`} className="h-3.5 w-3.5 fill-[#004C54] text-[#004C54]" />
                      ))}
                    </div>
                    <p className="text-[14px] text-[#004C54] leading-relaxed flex-1 mb-5">
                      "{t.quote}"
                    </p>
                    <div className="flex items-center gap-2.5 pt-4 border-t border-border/30">
                      <div className="relative w-10 h-10 shrink-0">
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#004C54] via-[#006873] to-[#003a40] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.18)]" />
                        <div className="absolute inset-[2px] rounded-full bg-gradient-to-br from-[#005860] to-[#004047] flex items-center justify-center ring-2 ring-[#4FBCC7]/40">
                          <span className="font-heading text-[11.5px] font-bold text-white tracking-wide drop-shadow-sm">
                            {t.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-[13px] font-semibold text-[#1a1a1a] dark:text-white leading-tight">{t.name}</p>
                        <p className="text-[11px] text-[#004C54]">
                          <span className="inline-block px-1.5 py-0.5 rounded bg-[#004C54]/8 text-[#004C54] font-semibold text-[10px] uppercase tracking-wider mr-1">{t.sport}</span>
                          {t.role}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
